<template>
  <section class="player-bar" v-show="isSupported">
    <div class="inner">
      <div class="current-track">
        <div class="image">
          <img v-if="episode.image" :src="episode.image.url" :alt="episode.image.alt">
        </div>
        <div class="text">
          <span>{{ episode.title }}</span>
          <p v-if="episode.subtitle">{{ episode.subtitle }}</p>
        </div>
      </div>
      <div class="track-controls">
        <div class="main-controls">
          <button class="prev" @click.prevent="handlePreviousTrack" :disabled="!isHasPreviousTrack"><i
              class="icon-prev"></i><span class="sr-only">Play Previous Track</span>
          </button>
          <button class="backward" @click.prevent="handleAddTime(-15)"><i class="icon-backward"></i><span
              class="sr-only">Rewind 15 sec.</span></button>
          <button class="play" @click.prevent="handlePlayButton">
            <i :class="{'icon-play': !this.isPlaying, 'icon-pause': this.isPlaying}"></i>
            <span class="sr-only">{{ this.isPlaying ? 'Pause' : 'Play' }}</span>
          </button>
          <button class="forward" @click.prevent="handleAddTime(15)"><i class="icon-rewind"></i><span class="sr-only">Fast forward 15 sec.</span>
          </button>
          <button class="next" @click.prevent="handleNextTrack" :disabled="!isHasNextTrack"><i
              class="icon-next"></i><span class="sr-only">Play Next Track</span>
          </button>
        </div>
        <div class="timeline">
          <span>{{ currentProgress }}</span>
          <div class="timeline-bar" @click="onProgressClickListener" ref="timeline">
            <div class="timeline-bar--active" @click="onProgressClickListener" :style="progressStyles"></div>
            <button v-dragged="onSeekListener" :style="{left: `${progress}%`}" class="timeline-bar--position">
              <span class="sr-only">Seek position</span>
            </button>
          </div>
          <span>{{ durationFormatted }}</span>
        </div>
      </div>
      <div class="right-controls">
        <div class="volume-control">
          <button class="mute" @click.prevent="handleMute">
            <i :class="{'icon-volume': !isMuted, 'icon-muted': isMuted}"></i>
            <span class="sr-only">{{ isMuted ? 'Unmute' : 'Mute' }}</span></button>
          <div class="volume-bar" @click="onVolumeClickListener">
            <div class="volume-bar--active" :style="volumeStyles"></div>
          </div>
        </div>
      </div>
      <video ref="player"
             style="display: none"
             :muted="isMuted"
             playsinline
             @loadstart="onLoadStartListener"
             @volumechange="onVolumeChangeListener"
             @play="onPlayListener"
             @pause="onPauseListener"
             @canplaythrough="onCanPlayThroughListener"
             @durationchange="onDurationChangeListener"
             @timeupdate="onTimeUpdateListener"
      ></video>
    </div>
  </section>
</template>

<script>
import Hls from 'hls.js'
import {formatSeconds} from "../../utils";

export default {
  props: {
    episode: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    isHasPreviousTrack: {
      type: Boolean,
      default: false,
    },
    isHasNextTrack: {
      type: Boolean,
      default: false,
    }
  },
  data: () => ({
    isSeeking: false,
    isMuted: false,
    isPlaying: false,
    volume: 1,
    currentTime: 0,
    duration: 0,
    progress: 0,
    seekProgress: 0,
    wrapperWidth: 0,
    player: null,
    hlsInstance: null,
    isSupported: true
  }),
  computed: {
    currentProgress() {
      return formatSeconds(this.currentTime)
    },
    durationFormatted() {
      return formatSeconds(this.duration)
    },
    progressStyles() {
      return {
        width: `${this.progress}%`
      }
    },
    volumeStyles() {
      const volumeWidth = this.volume * 100
      return {
        width: `${volumeWidth}%`
      }
    }
  },
  mounted() {
    this.$store.commit('podcast/setEpisode', this.episode)

    this.$nextTick(() => {
      this.initHlsPlayer()
    })
  },
  watch: {
    data: {
      deep: true,
      handler: function () {
        console.log('data changed')
        const isPlayingAlready = this.isPlaying

        this.$store.commit('podcast/setEpisode', this.episode)

        this.player.pause();

        const mediaSrc = this.data.stream_url;

        if (this.hlsInstance && mediaSrc) {
          this.hlsInstance.detachMedia()
          this.hlsInstance.loadSource(mediaSrc);
          this.hlsInstance.attachMedia(this.$refs.player);

          this.hlsInstance.once(Hls.Events.MANIFEST_PARSED, () => {
            this.player.currentTime = 0
            isPlayingAlready && this.player.play()
          });
        } else if (this.player.canPlayType('application/vnd.apple.mpegurl')) {
          this.player.src = mediaSrc;

          this.player.currentTime = 0
          isPlayingAlready && this.player.play()
        }
      }
    },
    isPlaying(value) {
      this.$store.commit('podcast/setIsPlaying', value)
    },
    volume(value) {
      this.isMuted = value === 0;
    },
    currentTime(value) {
      if (!this.isSeeking && this.duration) {
        this.progress = ((value * 100) / this.duration)
      }
    }
  },
  methods: {
    initHlsPlayer() {
      console.log('initHlsPlayer')
      this.player = this.$refs.player

      const mediaSrc = this.data.stream_url;

      if (Hls.isSupported()) {
        this.hlsInstance = new Hls();
        this.hlsInstance.loadSource(mediaSrc);
        this.hlsInstance.attachMedia(this.$refs.player);
      } else if (this.player.canPlayType('application/vnd.apple.mpegurl')) {
        this.player.src = mediaSrc;
      } else {
        alert('Your browser is not supported.')
        this.isSupported = false;
      }
    },
    handlePlayButton() {
      console.log('handlePlayButton')
      if (this.isPlaying) {
        this.player.pause();
      } else {
        this.player.play();
      }
    },
    handleAddTime(time) {
      console.log('handleAddTime')
      this.player.currentTime += time
    },
    handlePreviousTrack() {
      console.log('handlePreviousTrack')
      this.$emit('previous-track');
    },
    handleNextTrack() {
      console.log('handleNextTrack')
      this.$emit('next-track');
    },
    handleMute() {
      console.log('handleMute')
      this.isMuted = !this.isMuted
    },
    onPlayListener() {
      console.log('onPlayListener')
      this.isPlaying = true;
    },
    onPauseListener() {
      console.log('onPauseListener')
      this.isPlaying = false;
    },
    onCanPlayThroughListener() {
      console.log('onCanPlayThroughListener')
      this.duration = this.player.duration;
    },
    onDurationChangeListener() {
      console.log('onDurationChangeListener')
      this.duration = this.player.duration
    },
    onLoadStartListener() {
      console.log('onLoadStartListener')
      this.player.volume = this.volume;
    },
    onVolumeChangeListener() {
      console.log('onVolumeChangeListener')
      this.volume = this.player.volume;
    },
    onTimeUpdateListener() {
      console.log('onTimeUpdateListener')
      if (this.player) {
        this.currentTime = this.player.currentTime;
      }
    },
    onProgressClickListener(e) {
      console.log('onProgressClickListener')
      if (this.isSeeking) {
        return;
      }

      let target = e.target || e.srcElement
      this.wrapperWidth = this.wrapperWidth || target.offsetWidth
      let seekWidth = e.offsetX

      const percent = (seekWidth / this.wrapperWidth) * 100

      this.progress = percent
      this.player.currentTime = this.duration * (percent / 100)
    },
    onVolumeClickListener(e) {
      console.log('onVolumeClickListener')
      let target = e.target || e.srcElement
      const wrapperWidth = target.offsetWidth
      let seekWidth = e.offsetX

      let volume = seekWidth / wrapperWidth;

      if (volume < 0) {
        volume = 0;
      } else if (volume > 100) {
        volume = 100;
      }

      this.player.volume = volume
    },
    onSeekListener({offsetX, first, last}) {
      console.log('onSeekListener')
      if (first) {
        this.isSeeking = true
        this.seekProgress = this.progress
        return
      }
      if (last) {
        this.seekProgress = 0
        this.player.currentTime = this.duration * (this.progress / 100)

        setTimeout(() => {
          this.isSeeking = false
        })
        return;
      }

      if (offsetX) {
        const timelineWidth = this.$refs.timeline.offsetWidth
        const newProgressInPixels = ((timelineWidth / 100) * this.seekProgress) + offsetX

        const newProgress = ((newProgressInPixels / timelineWidth) * 100).toFixed()

        if (newProgress > 100) {
          this.progress = 100
        } else if (newProgress < 0) {
          this.progress = 0
        } else {
          this.progress = newProgress
        }
      }
    }
  }
}
</script>
